import React, { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  Badge,
  BadgeSize,
  BadgeType,
  Button,
  ButtonColor,
  TooltipInfo,
  Icon,
  IconType,
  ButtonStyle,
  NotificationType,
} from '../../../../../components'
import { CollaborationStatus as CollaborationStatusComponent } from '../../../../../components/CollaborationStatus'
import { Avatar } from '../../../../../elements'
import {
  BrandServiceLevel,
  CampaignAddon,
  Collaboration,
  CollaborationStatus,
  MutationMarkApplicantAsFavoriteArgs,
  MutationRemoveApplicantAsFavoriteArgs,
} from '../../../../../gql/types'
import { BrandRoutes } from '../../../../BrandRoutes'
import { AcceptContentModal } from '../../../../shared/AcceptContentModal'
import { CollaborationClipsSlider } from '../../../../shared/CollaborationClipsSlider'
import { ShipProductModal } from '../../../../shared/ShipProductModal'
import { UpdateProductTrackingLinkModal } from '../../../../shared/UpdateProductTrackingLinkModal'
import { CollaborationStatusTime } from '../../../components/CollaborationStatusTime'
import { ViewCreatorSlider } from '../../../../shared/ViewCreatorSlider'
import { OrderAddonsVariationsModal } from '../../../../shared/OrderAddonsVariationsModal'
import { useNotification } from '../../../../../utils/hooks'
import { useMutation } from '@apollo/client'
import {
  MARK_APPLICANT_AS_FAVORITE,
  REMOVE_APPLICANT_AS_FAVORITE,
} from '../../../../../gql'
import { mapAddonsToTooltipTextForBrand } from '../../../../../utils/mappers'
import { TipModal } from '../../../../shared/TipModal'

interface IProps {
  collaboration: Collaboration
}

export const CollaborationCard: React.FC<IProps> = ({ collaboration }) => {
  const { id } = useParams()
  const { addNotification } = useNotification()
  const isEnterpriseOrWhiteGlove =
    collaboration.campaign.brand.serviceLevel ===
      BrandServiceLevel.enterprise ||
    collaboration.campaign.brand.serviceLevel === BrandServiceLevel.whiteGlove

  const [isShipProductModalOpen, setIsShipProductModalOpen] = useState(false)
  const [
    isUpdateProductTrackingLinkModalOpen,
    setIsUpdateProductTrackingLinkModalOpen,
  ] = useState(false)
  const [isViewClipsSliderOpen, setIsViewClipsSliderOpen] = useState(false)
  const [isOrderOpen, setIsOrderOpen] = useState(false)
  const [isAcceptContentModalOpen, setIsAcceptContentModalOpen] =
    useState(false)
  const [isViewProfileSliderOpen, setIsViewProfileSliderOpen] = useState(false)
  const [isTipModalOpen, setIsTipModalOpen] = useState(false)

  const [markApplicantAsFavorite, { loading: markApplicantAsFavoriteLoading }] =
    useMutation<Collaboration, MutationMarkApplicantAsFavoriteArgs>(
      MARK_APPLICANT_AS_FAVORITE,
    )
  const [
    removeApplicantAsFavorite,
    { loading: removeApplicantAsFavoriteLoading },
  ] = useMutation<Collaboration, MutationRemoveApplicantAsFavoriteArgs>(
    REMOVE_APPLICANT_AS_FAVORITE,
  )

  const onMarkApplicantAsFavorite = async () => {
    try {
      await markApplicantAsFavorite({
        variables: {
          collaborationId: collaboration.id,
        },
      })

      addNotification(
        'Collaborator has been marked as a favorite.',
        NotificationType.success,
      )
    } catch (error) {
      console.log(error)
      addNotification(
        'There has been an error marking this applicant as favorite',
        NotificationType.error,
      )
    }
  }

  const onRemoveApplicantAsFavorite = async () => {
    try {
      await removeApplicantAsFavorite({
        variables: {
          collaborationId: collaboration.id,
        },
      })

      addNotification(
        'Collaborator has been removed from favorites.',
        NotificationType.success,
      )
    } catch (error) {
      console.log(error)
      addNotification(
        'There has been an error removing this applicant as favorite',
        NotificationType.error,
      )
    }
  }

  const RowButton = (collaboration: Collaboration) => {
    switch (collaboration.status) {
      case CollaborationStatus.creatorAccepted:
        return (
          <Button
            title='Ship product'
            type='button'
            colour={ButtonColor.primary}
            onClick={() => {
              setIsShipProductModalOpen(true)
            }}
          />
        )
      case CollaborationStatus.contentUploaded:
        return (
          <Button
            title='Clips'
            type='button'
            colour={ButtonColor.primary}
            onClick={() => setIsViewClipsSliderOpen(true)}
          />
        )
      case CollaborationStatus.contentAccepted:
        return (
          <Button
            title='Clips'
            type='button'
            colour={ButtonColor.primary}
            onClick={() => setIsViewClipsSliderOpen(true)}
          />
        )
      case CollaborationStatus.creatorApplied:
      case CollaborationStatus.productShipped:
      case CollaborationStatus.productDelivered:
        return undefined
        return (
          <Button
            title='Profile'
            type='button'
            colour={ButtonColor.primary}
            onClick={() => setIsViewProfileSliderOpen(true)}
          />
        )
    }
  }

  return (
    <>
      <td className='hidden'>
        <CollaborationClipsSlider
          open={isViewClipsSliderOpen}
          setOpen={setIsViewClipsSliderOpen}
          collaboration={collaboration}
        />

        <ShipProductModal
          collaboration={collaboration}
          isOpen={isShipProductModalOpen}
          setIsOpen={setIsShipProductModalOpen}
        />

        <AcceptContentModal
          collaboration={collaboration}
          isOpen={isAcceptContentModalOpen}
          setIsOpen={setIsAcceptContentModalOpen}
        />

        <UpdateProductTrackingLinkModal
          collaboration={collaboration}
          isOpen={isUpdateProductTrackingLinkModalOpen}
          setIsOpen={setIsUpdateProductTrackingLinkModalOpen}
        />

        <ViewCreatorSlider
          open={isViewProfileSliderOpen}
          setOpen={setIsViewProfileSliderOpen}
          creator={collaboration.creator}
          collaboration={collaboration}
        />
      </td>
      <td className='whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6'>
        <Link
          to={BrandRoutes.collaborationViewProfile
            .replace(':id', `${collaboration.campaign.brand.id}`)
            .replace(':uuid', collaboration.uuid)}
          className='cursor-pointer flex gap-2 font-medium text-gray-900 hover:text-gray-800'
        >
          <div className='flex items-center'>
            <Avatar
              type='user'
              src={collaboration.creator.avatar?.url}
              size='sm'
              alt={collaboration.creator.firstName || ''}
            />
            <div className='ml-2'>
              <div>{collaboration.creator.firstName}</div>
              <div className='truncate mb-0 flex flex-shrink-0 text-blue-600 hover:text-blue-500'>
                view profile
              </div>
            </div>
          </div>
        </Link>
      </td>

      <td className='whitespace-nowrap px-3 py-4 text-sm text-gray-500'>
        <CollaborationStatusComponent
          status={collaboration.status}
          onProductShippingClick={() =>
            setIsUpdateProductTrackingLinkModalOpen(true)
          }
        />
      </td>

      <td className='whitespace-nowrap px-3 py-4 text-sm text-gray-500'>
        {collaboration?.hooksCount > 0 && (
          <div className='mt-1 mb-0'>
            <Badge
              type={BadgeType.indigo}
              iconPosition='left'
              text={
                <TooltipInfo
                  valueKey='hooks-badge'
                  content={`${collaboration?.hooksCount} variations`}
                  tooltipText='Each variation is an altered version of the original video, featuring a unique hook or call-to-action. The duration of each variation is up to 5 seconds.'
                />
              }
              size={BadgeSize.small}
              className='!mr-0'
            />
          </div>
        )}
        {collaboration?.addonsCount > 0 && (
          <div className='mt-1 mb-0'>
            <Badge
              type={BadgeType.purple}
              iconPosition='left'
              text={
                <TooltipInfo
                  valueKey='addons-badge'
                  content={`${collaboration?.addonsCount} add-on${collaboration?.addonsCount > 1 ? 's' : ''} required`}
                  tooltipText={mapAddonsToTooltipTextForBrand(
                    collaboration.addons.map((addon) => addon.description),
                  )}
                />
              }
              size={BadgeSize.small}
              className='!mr-0'
            />
          </div>
        )}
        {collaboration?.hooksCount === 0 &&
          collaboration?.addonsCount === 0 && <p className=''>-</p>}
      </td>

      <td className='whitespace-nowrap px-3 py-4 text-sm text-gray-500'>
        <CollaborationStatusTime
          status={collaboration.status}
          updatedAt={collaboration.updatedAt}
        />
      </td>

      <td className='relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6 space-x-2'>
        {collaboration.isFavorite && (
          <Button
            type='button'
            styleType={ButtonStyle.ghost}
            onClick={onRemoveApplicantAsFavorite}
            loading={removeApplicantAsFavoriteLoading}
            disabled={removeApplicantAsFavoriteLoading}
            icon={
              <Icon
                type={IconType.heartFilled}
                className='h-4 w-4 text-red-500'
                aria-hidden='true'
              />
            }
            iconPosition='left'
            tooltip='Remove collaborator as shortlisted'
          />
        )}
        {!collaboration.isFavorite && (
          <Button
            type='button'
            styleType={ButtonStyle.ghost}
            onClick={onMarkApplicantAsFavorite}
            loading={markApplicantAsFavoriteLoading}
            disabled={markApplicantAsFavoriteLoading}
            icon={
              <Icon
                type={IconType.heartFilled}
                className='h-4 w-4 text-gray-400'
                aria-hidden='true'
              />
            }
            iconPosition='left'
            tooltip='Mark collaborator as shortlisted'
          />
        )}
        {collaboration.isActive && (
          <Button
            type='button'
            colour={ButtonColor.white}
            to={
              BrandRoutes.inboxChat
                .replace(':id', `${id}`)
                .replace(':uuid', `${id}-${collaboration.creator.id}`) +
              '?collaborationId=' +
              collaboration.id
            }
            icon={
              <Icon
                type={IconType.inbox}
                className='h-4 w-4'
                aria-hidden='true'
              />
            }
            iconPosition='left'
            tooltip='Message'
          />
        )}
        {collaboration.status === CollaborationStatus.contentAccepted && (
          <Button
            type='button'
            icon={
              <Icon
                type={IconType.money}
                className='h-4 w-4'
                aria-hidden='true'
              />
            }
            iconPosition='left'
            colour={ButtonColor.tertiary}
            onClick={() => setIsTipModalOpen(true)}
            tooltip='Reward the creator'
          />
        )}
        {!isEnterpriseOrWhiteGlove && (
          <Button
            // title='Buy more add-ons/variations'
            type='button'
            colour={ButtonColor.secondary}
            onClick={() => setIsOrderOpen(true)}
            icon={
              <Icon
                type={IconType.plus}
                className='h-4 w-4 text-white'
                aria-hidden='true'
              />
            }
            iconPosition='left'
            tooltip='Buy more variations/add-ons'
          />
        )}

        {RowButton(collaboration)}

        {!isEnterpriseOrWhiteGlove && setIsOrderOpen && (
          <OrderAddonsVariationsModal
            collaboration={collaboration}
            open={isOrderOpen}
            setOpen={setIsOrderOpen}
          />
        )}

        <TipModal
          brand={collaboration.campaign.brand}
          creator={collaboration.creator}
          open={isTipModalOpen}
          setOpen={setIsTipModalOpen}
        />
      </td>
    </>
  )
}
